/**
 * Where the developer's own credential lives.
 *
 * <b>This is a second, separate session, and the separation is the design.</b> `platform/api/session.js`
 * holds an ASP.NET Identity bearer token - the credential somebody who <i>administers</i> this service
 * signs in with. What is held here is a `covdbg_cs_` client session: the credential a person whose code
 * runs through the service holds, minted by a sign-in link and carried by their CLI and editor extension
 * too. The two populations overlap and are not the same table, and conflating them would mean every
 * developer needing a portal account.
 *
 * <b>This belongs to the end-user application alone, and that is newly true.</b> The portal used to write
 * it - that was how a borrowed view was handed over, both bundles being served from one origin. They are
 * deployed apart now, so what crosses is a single-use code and this application is the only one that ever
 * holds a client session.
 *
 * What talks to `/api/v1/seats/**` with it is `user/api/developer.js`, and deliberately not the
 * platform's `request`: that one refreshes Identity tokens, retries 401s against
 * `/api/v1/auth/refresh` and clears the *other* session when it fails. A client session has none of
 * those things - it lasts a year, is revoked rather than refreshed, and a 401 means what it says.
 */

const STORAGE_KEY = 'covdbg.developer';

let current = read();

const listeners = new Set();

function read()
{
   try
   {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;

      // An expired session is treated as no session at all, rather than as one the first request will
      // find out about: a borrowed view that ran out should stop looking signed in immediately.
      return parsed?.token && (!parsed.expiresAt || Date.parse(parsed.expiresAt) > Date.now())
         ? parsed
         : null;
   }
   catch
   {
      return null;
   }
}

function write(session)
{
   current = session;

   if (session)
   {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
   }
   else
   {
      localStorage.removeItem(STORAGE_KEY);
   }

   for (const listener of listeners)
   {
      listener(current);
   }
}

export function getDeveloper()
{
   return current;
}

export function isDeveloperSignedIn()
{
   return Boolean(read()?.token);
}

/**
 * Stores a session. `impersonatedBy` names the operator when the view is a borrowed one.
 *
 * Kept in the stored record rather than derived from the token, because the whole point of marking an
 * impersonation is that the borrowed view says so on every page - and the token is opaque.
 */
export function setDeveloper(session)
{
   write(session);
}

export function clearDeveloper()
{
   write(null);
}

export function onDeveloperChange(listener)
{
   listeners.add(listener);
   return () => listeners.delete(listener);
}

